import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, AppBarProps, Badge, styled, Toolbar } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ReactComponent as BackIcon } from 'src/assets/icons/header/back-icon.svg';
import { ReactComponent as BellIcon } from 'src/assets/icons/header/bell-icon.svg';
import { ReactComponent as CloseIcon } from 'src/assets/icons/header/close-icon.svg';
import { ReactComponent as HomeIcon } from 'src/assets/icons/header/home-icon.svg';
import { ReactComponent as LogoIcon } from 'src/assets/icons/header/logo-icon.svg';
import { ReactComponent as MoreIcon } from 'src/assets/icons/header/more-icon.svg';
import { ReactComponent as UserIcon } from 'src/assets/icons/header/user-icon.svg';
import { HEADER_HEIGHT } from 'src/assets/static/constants';
import { PATH_PAGE } from 'src/routes/paths';
import { notificationApi } from 'src/services';

type LeftType = 'back' | 'logo';
type RightType = 'home' | 'close' | 'notification' | 'myPage' | 'more' | 'custom';
type CenterType = 'title';

export interface HeaderProps extends AppBarProps {
  leftTypes?: LeftType[] | 'none';
  centerType?: CenterType;
  centerTitle?: string;
  rightTypes?: RightType[] | 'none';
  onClickLeft?: (type: LeftType) => void;
  onClickCenter?: () => void;
  onClickRight?: (type: RightType) => void;
  customLeft?: React.ReactNode;
  customCenter?: React.ReactNode;
  customRight?: React.ReactNode;
}

const Header = ({
  leftTypes = 'none',
  centerType = 'title',
  centerTitle = '',
  rightTypes = 'none',
  onClickLeft,
  onClickCenter,
  onClickRight,
  customLeft,
  customCenter,
  customRight,
  ...props
}: HeaderProps) => {
  const navigate = useNavigate();
  const [hasNotification, setHasNotification] = React.useState(false);

  // 푸시 알림 조회
  React.useEffect(() => {
    if (rightTypes !== 'none' && rightTypes.includes('notification')) {
      const fetchNotifications = async () => {
        const result = await notificationApi.getNotification();
        // if (result?.resultCode === 'S' && result.data && result.data.length > 0) {
        if (
          result?.resultCode === 'S' &&
          result.data &&
          result.data.filter((item) => item.checkYn === 'N').length > 0
        ) {
          setHasNotification(true);
        } else {
          setHasNotification(false);
        }
      };

      fetchNotifications();
    }
  }, [rightTypes]);

  const renderIcon = (type: LeftType | RightType) => {
    switch (type) {
      case 'back':
        return <BackIcon />;
      case 'logo':
        return <LogoIcon className="logo" style={{ width: '60px', height: '60px' }} />;
      case 'home':
        return <HomeIcon />;
      case 'close':
        return <CloseIcon />;
      case 'notification':
        return hasNotification ? (
          <Badge
            color="primary"
            variant="dot"
            sx={{
              '.MuiBadge-badge': {
                width: '7px',
                height: '7px',
              },
            }}
          >
            <BellIcon />
          </Badge>
        ) : (
          <BellIcon />
        );
      case 'myPage':
        return <UserIcon />;
      case 'more':
        return <MoreIcon />;
      default:
        return null;
    }
  };

  const handleClickRight = (type: RightType) => {
    if (onClickRight) {
      onClickRight(type);
      return;
    }

    switch (type) {
      case 'home':
        return navigate(PATH_PAGE.home);
      case 'close':
        return navigate(-1);
      case 'notification':
        return navigate(PATH_PAGE.notification);
      case 'myPage':
        return navigate(PATH_PAGE.myPage.root);
      case 'more':
        return;
    }
  };

  const handleClickLeft = (type: LeftType) => {
    if (onClickLeft) {
      onClickLeft(type);
      return;
    }

    switch (type) {
      case 'back':
        return navigate(-1);
      case 'logo':
        return navigate(PATH_PAGE.home);
    }
  };

  const handleClickCenter = () => {
    if (onClickCenter) {
      onClickCenter();
      return;
    }

    switch (centerType) {
      default:
        return null;
    }
  };

  const renderRight = () => {
    if (rightTypes === 'none') return null;

    if (customRight) {
      return customRight;
    }

    return (
      <>
        {rightTypes.map((type) => {
          return (
            <IconWrapper
              key={`right-menu-${type}`}
              onClick={() => {
                handleClickRight(type);
              }}
              sx={{ cursor: 'pointer' }}
            >
              {renderIcon(type)}
            </IconWrapper>
          );
        })}
      </>
    );
  };

  const renderLeft = () => {
    if (leftTypes === 'none') return null;

    if (customLeft) {
      return customLeft;
    }

    return (
      <>
        {leftTypes.map((type) => {
          return (
            <IconWrapper
              key={`left-menu-${type}`}
              onClick={() => {
                handleClickLeft(type);
              }}
              sx={{ cursor: 'pointer' }}
            >
              {renderIcon(type)}
            </IconWrapper>
          );
        })}
      </>
    );
  };

  const renderCenter = () => {
    if (customCenter) {
      return customCenter;
    }

    switch (centerType) {
      case 'title':
        return <TitleText>{centerTitle}</TitleText>;
      default:
        return null;
    }
  };

  return (
    <Container {...props}>
      <HeaderContentContainer>
        {/* Left */}
        <LeftItem id="header-left-menu">{renderLeft()}</LeftItem>
        {/* Center */}
        <CenterItem
          onClick={handleClickCenter}
          sx={{ cursor: onClickCenter ? 'pointer' : 'default' }}
        >
          {renderCenter()}
        </CenterItem>
        {/* Right */}
        <RightItem id="header-right-menu">{renderRight()}</RightItem>
      </HeaderContentContainer>
    </Container>
  );
};

export default observer(Header);

const Container = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none',
}));

const HeaderContentContainer = styled(Toolbar)(({ theme }) => ({
  height: `${HEADER_HEIGHT}px`,
  width: '100%',
  margin: 'auto',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(3),
}));

const Item = styled('div')({
  minWidth: 40,
  minHeight: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',
});

const LeftItem = styled(Item)({});

const CenterItem = styled(Item)({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '80%',
  pointerEvents: 'none',
  overflow: 'hidden',
});

const RightItem = styled(Item)({});

const TitleText = styled('h3')(({ theme }) => ({
  fontSize: '20px',
  lineHeight: 1.2,
  fontWeight: 500,
  textAlign: 'center',
  color: theme.palette.text.primary,
  margin: 0,
  pointerEvents: 'auto',

  // 한줄 넘어서면 ... 처리
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const IconWrapper = styled('div')({
  minWidth: 40,
  minHeight: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  userSelect: 'none',
});
